import { useEffect, useReducer, useRef } from 'react';
import { injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { useDIContainer } from '@/container/RouteContainerProvider';
import { ASSIST_FIELDS, type AssistFieldDef } from '@/shared/assist/AssistContract';
import { mergeFieldDefs, type DynamicDimension } from '@/shared/assist/FieldCatalog';
import { contextClient } from '@/shared/ContextApiClient';
import { registerFieldLabels } from '@/shared/analytics/MetricFormatting';

/**
 * Loads the tenant's classifier dimensions from the context service once and
 * merges them with the curated static {@link ASSIST_FIELDS}, so every assist
 * surface (Add-insight modal, filter editor) can offer the full field vocabulary
 * and {@link assistFieldLabel} can resolve dynamic labels. A DI singleton that
 * starts with the static set and notifies subscribers when the dynamic catalog
 * arrives; on failure it silently keeps the static set.
 */
@injectable()
export class FieldCatalogService extends BaseViewModel {
    private merged: AssistFieldDef[] = ASSIST_FIELDS;
    private started = false;

    /** The current merged field catalog (static immediately; dynamic once loaded). */
    fields(): AssistFieldDef[] {
        return this.merged;
    }

    /** Triggers the one-time dynamic load (idempotent). */
    ensureLoaded(): void {
        if (this.started) return;
        this.started = true;
        void this.load();
    }

    private async load(): Promise<void> {
        try {
            const dimensions = await contextClient.dimensionsApi.getDimensions();
            const dynamic: DynamicDimension[] = dimensions
                .filter((dimension) => dimension.isActive)
                .map((dimension) => ({ key: dimension.key, label: dimension.label, description: dimension.description }));
            this.merged = mergeFieldDefs(dynamic);
            registerFieldLabels(this.merged);
            this.notifyChange();
        } catch {
            return;
        }
    }
}

/**
 * Resolves the shared {@link FieldCatalogService} singleton, triggers its load,
 * and subscribes to re-render when the dynamic catalog arrives. Returns the
 * merged field list. (Manual subscribe, like `usePinnedTilesStore` — never
 * `useViewModel`, which would dispose the shared singleton on unmount.)
 */
export function useFieldCatalog(): AssistFieldDef[] {
    const container = useDIContainer();
    const serviceRef = useRef<FieldCatalogService | null>(null);
    if (serviceRef.current === null) serviceRef.current = container.resolve(FieldCatalogService);
    const service = serviceRef.current;
    const [, forceUpdate] = useReducer((count: number) => count + 1, 0);

    useEffect(() => {
        service.ensureLoaded();
        return service.subscribe(() => forceUpdate());
    }, [service]);

    return service.fields();
}
